import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (e, path, hash) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    
    if (path) {
      navigate(path);
    } else if (hash) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <div className="logo" onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_collective-build-1/artifacts/ev3zutd7_R6%20Box%20Logo%20Only%20.jpg" 
            alt="R6 Collective Logo" 
          />
        </div>
        
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`} data-testid="main-nav">
          <a href="#challenges" onClick={(e) => handleNavClick(e, null, '#challenges')}>Challenges</a>
          <a href="#framework" onClick={(e) => handleNavClick(e, null, '#framework')}>Framework</a>
          <a href="#approach" onClick={(e) => handleNavClick(e, null, '#approach')}>Approach</a>
          <a href="#about" onClick={(e) => handleNavClick(e, null, '#about')}>About</a>
          
          <div className="nav-dropdown" ref={servicesRef} data-testid="services-dropdown">
            <button 
              className={`nav-dropdown-trigger ${location.pathname === '/consulting-solutions' || location.pathname === '/the-right-path' || location.pathname === '/r6-advisory' ? 'active' : ''}`}
              onClick={() => setIsServicesOpen(!isServicesOpen)}
            >
              Services <ChevronDown size={14} className={`nav-dropdown-chevron ${isServicesOpen ? 'rotated' : ''}`} />
            </button>
            <div className={`nav-dropdown-menu ${isServicesOpen ? 'open' : ''}`}>
              <a href="/consulting-solutions" onClick={(e) => handleNavClick(e, '/consulting-solutions', null)} data-testid="nav-consulting">
                <span className="nav-dropdown-label">Consulting Solutions</span>
                <span className="nav-dropdown-desc">Leadership, talent & governance advisory</span>
              </a>
              <a href="/the-right-path" onClick={(e) => handleNavClick(e, '/the-right-path', null)} data-testid="nav-rightpath">
                <span className="nav-dropdown-label">The Right Path</span>
                <span className="nav-dropdown-desc">Workforce development program</span>
              </a>
              <a href="/r6-advisory" onClick={(e) => handleNavClick(e, '/r6-advisory', null)} data-testid="nav-r6-advisory">
                <span className="nav-dropdown-label">R6 Advisory</span>
                <span className="nav-dropdown-desc">Client portal login & subscription signup</span>
              </a>
            </div>
          </div>

          <a href="/people-and-places" onClick={(e) => handleNavClick(e, '/people-and-places', null)} className={location.pathname === '/people-and-places' ? 'active' : ''} data-testid="nav-people-places">People + Places</a>
          <a href="/capabilities" onClick={(e) => handleNavClick(e, '/capabilities', null)}>Right 6+ Capabilities</a>
          <a href="/r6-advisory" onClick={(e) => handleNavClick(e, '/r6-advisory', null)} className="client-login-link" data-testid="nav-client-login">R6 Advisory Login</a>
          <a href="/portal/signup" onClick={(e) => handleNavClick(e, '/portal/signup', null)} className="cta-link" data-testid="nav-client-signup">Get Started</a>
          <a href="https://form.jotform.com/260186646305054" className="cta-link cta-link-outline" target="_blank" rel="noopener noreferrer">Get in Touch</a>
        </nav>
      </div>
    </header>
  );
};

export default Header;